import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    // Oktagon (THEME.md §9.4 #34, T-175): no video for visitors who reduce motion or save data; the poster stays
    if (prefersReducedMotion() || /** @type {any} */ (navigator).connection?.saveData) {
      this.dataset.oktStill = '';
      return;
    }

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }

  /**
   * Oktagon (THEME.md §9.4 #34, T-175): the pause control (snippets/okt-video-toggle.liquid) pauses and plays the video
   * and names its next action.
   */
  oktToggle() {
    const { videoElement } = this.refs;
    const paused = !videoElement.paused;

    if (paused) {
      videoElement.pause();
    } else {
      videoElement.play().catch(() => {});
    }
    this.toggleAttribute('data-okt-paused', paused);

    const button = this.querySelector('.okt-video-toggle');
    if (button instanceof HTMLElement) {
      button.setAttribute('aria-label', (paused ? button.dataset.labelPlay : button.dataset.labelPause) ?? '');
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
